/* TDT · Hero */
if (!customElements.get('tdt-hero')) {
  class TdtHero extends HTMLElement {
    connectedCallback() {
      this.events = new AbortController();
      this.listenerOptions = { signal: this.events.signal };
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.userPaused = false;
      this.editorPaused = false;
      this.focused = false;
      this.pauseButton = this.querySelector('[data-pause]');
      this.pauseButton?.addEventListener('click', () => {
        this.userPaused = !this.userPaused;
        this.pauseButton.setAttribute('aria-pressed', String(this.userPaused));
        this.pauseButton.textContent = this.userPaused ? this.pauseButton.dataset.resumeLabel : this.pauseButton.dataset.pauseLabel;
        if (this.userPaused) { this.stop(); this.pauseMedia(this.slides[this.index]); }
        else { this.focused = false; this.start(); this.playMedia(this.slides[this.index]); }
      }, this.listenerOptions);
      this.motion.addEventListener('change', () => {
        if (this.motion.matches) { this.stop(); this.slides.forEach(slide => this.pauseMedia(slide)); }
        else { this.start(); this.playMedia(this.slides[this.index]); }
      }, this.listenerOptions);
      this.track = this.querySelector('.tdt-hero__track');
      this.slides = Array.from(this.querySelectorAll('.tdt-hero__slide'));
      this.dots = Array.from(this.querySelectorAll('.tdt-hero__dot'));
      this.isSlide = this.classList.contains('tdt-hero--slide');
      this.index = 0;
      this.slides.forEach((slide, index) => { slide.inert = index !== 0; slide.classList.toggle('is-active', index === 0); if (index === 0) slide.removeAttribute('aria-hidden'); else slide.setAttribute('aria-hidden', 'true'); });
      this.interval = parseInt(this.dataset.interval, 10) || 6000;
      this.autoplay = this.dataset.autoplay === 'true' && this.slides.length > 1;
      this.style.setProperty('--tdt-hero-interval', this.interval + 'ms');

      this.querySelector('[data-prev]')?.addEventListener('click', () => this.go(this.index - 1, true), this.listenerOptions);
      this.querySelector('[data-next]')?.addEventListener('click', () => this.go(this.index + 1, true), this.listenerOptions);
      this.dots.forEach((dot) => dot.addEventListener('click', () => this.go(+dot.dataset.go, true), this.listenerOptions));

      this.initSwipe();
      this.initKeyboard();
      this.initVisibility();
      this.initEditor();

      this.playMedia(this.slides[0]);
      this.start();
    }

    disconnectedCallback() {
      this.stop();
      this.events?.abort();
      clearTimeout(this.resizeTimer);
      this.slides.forEach(slide => this.pauseMedia(slide));
      this.observer?.disconnect();
      document.removeEventListener('visibilitychange', this.onVisibility);
    }

    /* ---------- Navegación ---------- */
    go(i, user = false) {
      const total = this.slides.length;
      if (total < 2) return;
      const next = (i + total) % total;
      if (next === this.index) return;

      const prevSlide = this.slides[this.index];
      const nextSlide = this.slides[next];

      const moveFocus = prevSlide.contains(document.activeElement);
      prevSlide.inert = true;
      nextSlide.inert = false;
      prevSlide.classList.remove('is-active');
      prevSlide.setAttribute('aria-hidden', 'true');
      nextSlide.classList.add('is-active');
      nextSlide.removeAttribute('aria-hidden');
      if (moveFocus) nextSlide.querySelector('a, button')?.focus({ preventScroll: true });

      this.dots.forEach((d, k) => {
        d.classList.toggle('is-active', k === next);
        d.setAttribute('aria-selected', String(k === next));
      });

      if (this.isSlide) {
        this.track.style.transform = `translateX(-${next * 100}%)`;
      }

      this.pauseMedia(prevSlide);
      this.playMedia(nextSlide);
      this.index = next;

      if (user) this.restart();
    }

    /* ---------- Autoplay ---------- */
    start() {
      if (!this.autoplay || this.timer || this.userPaused || this.focused || this.editorPaused || this.motion.matches || document.hidden || this.inView === false) return;
      this.classList.remove('is-paused');
      this.timer = setInterval(() => this.go(this.index + 1), this.interval);
    }

    stop() {
      clearInterval(this.timer);
      this.timer = null;
      this.classList.add('is-paused');
    }

    restart() {
      this.stop();
      // Reinicia la animación de la barra de progreso
      const active = this.dots[this.index]?.querySelector('.tdt-hero__dot-progress');
      if (active) {
        active.style.animation = 'none';
        void active.offsetWidth;
        active.style.animation = '';
      }
      this.start();
    }

    /* ---------- Vídeos ---------- */
    visibleVideos(slide) {
      return Array.from(slide.querySelectorAll('video')).filter((v) => v.offsetParent !== null);
    }

    playMedia(slide) {
      if (!slide) return;
      if (this.motion.matches || this.userPaused || document.hidden || this.inView === false) { this.pauseMedia(slide); return; }
      this.visibleVideos(slide).forEach((v) => {
        v.muted = true;
        const p = v.play();
        if (p && p.catch) p.catch(() => {});
      });
    }

    pauseMedia(slide) {
      if (!slide) return;
      slide.querySelectorAll('video').forEach((v) => v.pause());
    }

    /* ---------- Swipe ---------- */
    initSwipe() {
      let x0 = null;
      let y0 = null;
      this.addEventListener('touchstart', (e) => {
        x0 = e.touches[0].clientX;
        y0 = e.touches[0].clientY;
      }, { passive: true, signal: this.events.signal });
      this.addEventListener('touchend', (e) => {
        if (x0 === null) return;
        const dx = e.changedTouches[0].clientX - x0;
        const dy = e.changedTouches[0].clientY - y0;
        if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy)) {
          this.go(this.index + (dx < 0 ? 1 : -1), true);
        }
        x0 = y0 = null;
      }, { passive: true, signal: this.events.signal });
    }

    initKeyboard() {
      this.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') this.go(this.index - 1, true);
        if (e.key === 'ArrowRight') this.go(this.index + 1, true);
      }, this.listenerOptions);
      // Pausa si el usuario navega con teclado dentro del hero (accesibilidad)
      this.addEventListener('focusin', () => { this.focused = true; this.stop(); }, this.listenerOptions);
      this.addEventListener('focusout', (e) => {
        if (!this.contains(e.relatedTarget)) { this.focused = false; this.start(); }
      }, this.listenerOptions);
    }

    /* ---------- Pausar fuera de pantalla / pestaña oculta ---------- */
    initVisibility() {
      this.onVisibility = () => {
        if (document.hidden) {
          this.stop();
          this.pauseMedia(this.slides[this.index]);
        } else if (this.inView !== false) {
          this.start();
          this.playMedia(this.slides[this.index]);
        }
      };
      document.addEventListener('visibilitychange', this.onVisibility, this.listenerOptions);

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(([entry]) => {
          this.inView = entry.isIntersecting;
          if (entry.isIntersecting) {
            this.start();
            this.playMedia(this.slides[this.index]);
          } else {
            this.stop();
            this.pauseMedia(this.slides[this.index]);
          }
        }, { threshold: 0.15 });
        this.observer.observe(this);
      }

      // Al cambiar entre móvil/escritorio, reproducir el vídeo visible
      window.addEventListener('resize', () => {
        clearTimeout(this.resizeTimer);
        this.resizeTimer = setTimeout(() => {
          this.slides.forEach((s) => this.pauseMedia(s));
          this.playMedia(this.slides[this.index]);
        }, 250);
      }, this.listenerOptions);
    }

    /* ---------- Editor de temas ---------- */
    initEditor() {
      if (!window.Shopify || !Shopify.designMode) return;
      const section = this.closest('.shopify-section');
      if (!section) return;
      document.addEventListener('shopify:block:select', (e) => {
        if (!section.contains(e.target)) return;
        const i = this.slides.indexOf(e.target);
        if (i > -1) {
          this.editorPaused = true;
          this.go(i);
          this.stop();
        }
      }, this.listenerOptions);
      document.addEventListener('shopify:block:deselect', (e) => {
        if (section.contains(e.target)) { this.editorPaused = false; this.start(); }
      }, this.listenerOptions);
    }
  }

  customElements.define('tdt-hero', TdtHero);
}
